import React from 'react';

import { ScrollView } from '@rbilabs/universal-components';
import { Icon, Pressable } from '@rbilabs/universal-components';
import { usePaymentContext } from 'features/payment';
import { CardTypes } from 'features/payment/types';
import { partition } from 'lodash-es';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { BottomSheet } from 'components/bottom-sheet';
import { classifyPaymentMethods } from 'components/payment-method/utils';
import { InlineModal } from 'components/ui';
import { useIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import { MastercardPartnershipAlert } from 'pages/loyalty/loyalty-partners/mastercard/mastercard-partnership-alert';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { getDesignToken, primitive, spacing } from 'styles/constants';
import { Region } from 'utils/environment/types';

import { SelectorOption } from './selector-option';
import { styles as commonStyles } from './styles';
import { SelectionType, SelectorProps } from './types';

type SelectorBottomSheetProps = SelectorProps & {
  isOpen: boolean;
  isPrepaidReload?: boolean;
  noPaymentMethodsAvailable?: boolean;
  onClose: () => void;
  onSelect: (type: SelectionType, id?: string) => void;
};

type PaymentSheetContentProps = Omit<SelectorBottomSheetProps, 'isOpen'> & {
  headerTitle?: string;
};

// bottom sheet is causing issues on iOS with lockups disable for now
const DISABLE_BOTTOM_SHEET = true;

const PaymentSheetContent = ({
  methods,
  selectedMethodId,
  isPrepaidReload,
  onSelect,
  onClose,
  headerTitle,
}: PaymentSheetContentProps) => {
  const { getPrepaidPaymentMethod } = usePaymentContext();
  const existsPrepaidMethod = getPrepaidPaymentMethod();
  const { bottom: safeAreaBottom } = useSafeAreaInsets();
  const { region } = useLocale();
  const isDesktopWeb = useIsDesktopWebBreakpoint();
  const isCARegion = region === Region.CA;

  const showMastercardPartnership = useFlag(LaunchDarklyFlag.SHOW_MASTERCARD_PARTNERSHIP);

  const { selectedMethod } = classifyPaymentMethods(methods, selectedMethodId ?? '');

  const prepaidActionText = existsPrepaidMethod ? t('transferBalance') : t('addGiftCard');

  // Place selected method at the top
  const sortedMethods = React.useMemo(() => {
    if (!selectedMethod) {
      return methods;
    }

    const [selected, others] = partition(
      methods,
      method => method.fdAccountId === selectedMethod.fdAccountId
    );

    return [...selected, ...others];
  }, [methods, selectedMethod]);

  return (
    <View style={styles.bottomSheetContent}>
      {!isDesktopWeb && !DISABLE_BOTTOM_SHEET && (
        <>
          {/* Header */}
          <View style={styles.headerContainer}>
            <View style={commonStyles.separator} />
            <Text style={styles.headerText}>{headerTitle}</Text>
            <View style={commonStyles.separator} />
            <Pressable
              accessible={true}
              accessibilityLabel={t('back')}
              accessibilityRole="button"
              onPress={onClose}
            >
              <Icon variant={'cancel'} color={getDesignToken('icon-default')} title={t('back')} />
            </Pressable>
          </View>
        </>
      )}

      <ScrollView showsVerticalScrollIndicator width="full">
        {/* Options */}
        {sortedMethods.map((method, i) => {
          return (
            <SelectorOption
              key={`method-${method.fdAccountId}`}
              bordered={i < sortedMethods.length - 1}
              option={method}
              selectedMethod={selectedMethod}
              onSelect={(type, id) => {
                onSelect(type, id);
              }}
            />
          );
        })}
      </ScrollView>

      <View
        style={[
          commonStyles.separator,
          {
            borderTopColor: primitive.$bbqBrown,
            borderTopWidth: 1,
            width: '100%',
            flexShrink: 1,
          },
        ]}
      />

      <View style={[styles.actionsContainer, { paddingBottom: safeAreaBottom }]}>
        <Pressable
          accessibilityLabel={t('addNewCard')}
          role="button"
          style={[
            commonStyles.option,
            styles.addNewCardOption,
            { borderBottomWidth: !isCARegion && !isPrepaidReload ? 1 : 0 },
          ]}
          onPress={() => onSelect('add')}
        >
          <View style={styles.addNewCardOptionContent}>
            <Icon accessibilityHidden variant="emptyCreditCard" size="md" />
            <Text style={styles.defaultText}>{t('addNewCard')}</Text>
          </View>
          {showMastercardPartnership &&
            !methods.some(method => method.credit?.cardType === CardTypes.MASTERCARD) && (
              <MastercardPartnershipAlert short />
            )}
        </Pressable>

        {!isCARegion && (
          <Pressable
            accessibilityLabel={prepaidActionText}
            role="button"
            style={commonStyles.option}
            onPress={() => onSelect('prepaid')}
          >
            <Icon accessibilityHidden variant="giftCard" size="md" />
            <Text style={styles.defaultText}>{prepaidActionText}</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
};

export const SelectorBottomSheet = ({
  isOpen = false,
  onClose,
  ...contentProps
}: SelectorBottomSheetProps) => {
  const isDesktopWeb = useIsDesktopWebBreakpoint();

  const { isPrepaidReload, noPaymentMethodsAvailable } = contentProps;
  let headerTitle = isPrepaidReload ? t('loadWith') : t('selectPaymentMethodDrawer');
  headerTitle = noPaymentMethodsAvailable ? t('loadWith') : headerTitle;

  if (isDesktopWeb || DISABLE_BOTTOM_SHEET) {
    return (
      <InlineModal
        header={{
          title: headerTitle,
          variant: 'transparent',
        }}
        isOpen={isOpen}
        backgroundColor="white"
        onClose={onClose}
        webSize={{ height: 'auto', width: '40%' }}
      >
        <PaymentSheetContent {...contentProps} onClose={onClose} />
      </InlineModal>
    );
  }
  return (
    <BottomSheet isOpen={isOpen} onOpenEventMessage="Payment Method Drawer" onClose={onClose}>
      <PaymentSheetContent onClose={onClose} headerTitle={headerTitle} {...contentProps} />
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  bottomSheetContent: {
    width: '100%',
    justifyContent: 'center',
    padding: spacing.$4,
    textAlign: 'center',
    alignItems: 'center',
    ...(Platform.OS === 'web' && {
      flex: 1,
    }),
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.$bbqBrown,
    fontSize: 18,
    fontWeight: 'normal',
    marginBottom: spacing.$4,
  },
  headerText: {
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.$bbqBrown,
    fontSize: 18,
    fontWeight: 'normal',
  },
  defaultText: {
    fontFamily: primitive.fontFamily.$body,
    color: primitive.$bbqBrown,
    fontSize: primitive.fontSizes.md,
    fontWeight: 'normal',
    marginLeft: spacing.$4,
  },
  actionsContainer: {
    width: '100%',
  },
  addNewCardOption: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: spacing.$4,
  },
  addNewCardOptionContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});
